import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Zap, X } from 'lucide-react';
import { trackEvent, EVENTS } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'guardian:plan_banner_dismissed_at';
const DISMISS_WINDOW_MS = 1000 * 60 * 60 * 24; // 24h re-show window

/**
 * PlanLimitBanner — shown to Free tenants when they approach or hit a soft limit.
 *
 * Props:
 *   resource    — which limit to check ('assets', 'users', 'work_orders_this_month', etc.)
 *                 Defaults to 'assets'.
 *   location    — context string sent with GA events (e.g. 'assets_page').
 *
 * Behaviour:
 *   - Fetches /api/tenant-settings/plan/usage on mount
 *   - Only renders for plan === 'free'
 *   - At 80% of limit: yellow "approaching limit" warning
 *   - At 100% of limit: red "limit reached" hard banner
 *   - Dismissible (24h), but the hard-limit banner always re-shows
 *   - Fires GA events: plan_limit_banner_view, upgrade_clicked, plan_limit_hit
 */
export const PlanLimitBanner = ({ resource = 'assets', location = 'assets_page', refreshKey = 0 }) => {
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetchUsage = async () => {
      try {
        const res = await fetch(`${API_URL}/api/tenant-settings/plan/usage`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('equipr_token')}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setUsage(data);
      } catch {
        /* silently ignore — banner just won't render */
      }
    };

    // Check dismissal window
    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_WINDOW_MS) {
      setDismissed(true);
    }

    fetchUsage();
  }, [refreshKey]);

  if (!usage || usage.plan !== 'free' || !usage.limits) return null;

  // Map resource → current/limit pair
  const limitMap = {
    assets: { current: usage.usage?.assets, limit: usage.limits?.assets, label: 'assets' },
    users: { current: usage.usage?.users, limit: usage.limits?.users, label: 'users' },
    work_orders_this_month: {
      current: usage.usage?.work_orders_this_month,
      limit: usage.limits?.work_orders_per_month,
      label: 'work orders this month',
    },
    inspections_this_month: {
      current: usage.usage?.inspections_this_month,
      limit: usage.limits?.inspections_per_month,
      label: 'inspections this month',
    },
    certificates: { current: usage.usage?.certificates, limit: usage.limits?.certificates, label: 'certificates' },
    task_templates: { current: usage.usage?.task_templates, limit: usage.limits?.task_templates, label: 'task templates' },
  };

  const { current, limit, label } = limitMap[resource] || {};
  if (current == null || limit == null) return null;

  const ratio = current / limit;
  const atLimit = current >= limit;
  const approaching = ratio >= 0.8 && !atLimit;

  if (!atLimit && !approaching) return null;
  if (dismissed && !atLimit) return null; // soft-limit can be dismissed; hard-limit cannot

  // Fire view event once per render-state change
  // (kept simple: not throttled — GA handles dedup on its own session/user model)
  if (typeof window !== 'undefined' && !window.__planBannerSeen) {
    window.__planBannerSeen = new Set();
  }
  const seenKey = `${resource}:${atLimit ? 'hit' : 'warn'}`;
  if (typeof window !== 'undefined' && !window.__planBannerSeen.has(seenKey)) {
    window.__planBannerSeen.add(seenKey);
    trackEvent(EVENTS.PLAN_LIMIT_BANNER_VIEW, { resource, current, limit, state: atLimit ? 'hit' : 'warn', location });
    if (atLimit) trackEvent(EVENTS.PLAN_LIMIT_HIT, { resource, current, limit, location });
  }

  const handleUpgrade = () => {
    trackEvent(EVENTS.UPGRADE_CLICKED, { location: `plan_limit_banner_${resource}`, current, limit });
    navigate('/pricing');
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  const isHard = atLimit;
  const bg = isHard ? 'bg-red-500/10 border-red-500/40' : 'bg-[#FFD108]/10 border-[#FFD108]/40';
  const iconColor = isHard ? 'text-red-400' : 'text-[#FFD108]';
  const title = isHard
    ? `You've reached your Free plan limit (${current}/${limit} ${label})`
    : `You're approaching your Free plan limit (${current}/${limit} ${label})`;
  const body = isHard
    ? 'Upgrade to Professional for unlimited usage. Field & mobile users are always free.'
    : `Once you hit ${limit}, you'll need to upgrade to add more. Field & mobile users are always free.`;

  return (
    <div
      className={`flex items-start gap-3 rounded-lg border ${bg} px-4 py-3`}
      data-testid={`plan-limit-banner-${resource}`}
    >
      <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${iconColor}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{body}</p>
      </div>
      <button
        onClick={handleUpgrade}
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#FFD108] text-black text-xs font-semibold uppercase tracking-wide hover:bg-[#E5B93D] transition-colors"
        data-testid={`plan-limit-upgrade-btn-${resource}`}
      >
        <Zap className="w-3.5 h-3.5" />
        Upgrade
      </button>
      {!isHard && (
        <button
          onClick={handleDismiss}
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors p-1"
          data-testid={`plan-limit-dismiss-btn-${resource}`}
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default PlanLimitBanner;
